import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

/* ================= COMPONENTS ================= */

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppFloat from "./components/WhatsAppFloat";
import ScrollToTop from "./components/ScrollToTop";

/* ================= PAGES ================= */

import HomePage from "./pages/Homepage";
import AboutPage from "./pages/AboutUs";
import SacMtbGame from "./pages/SacMtbGame";
import ProductDetailPage from "./pages/ProductDetailPage";
import ProductsPage from "./pages/ProductsPage";
import ProfilePage from "./pages/ProfilePage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import UserOrders from "./pages/Useroder";
import Toys from "./pages/Toys";
import PlayGame from "./pages/PlayGame";
import ComingSoon from "./pages/CommingSoon";

/* ================= AUTH PAGES ================= */

import RegisterPage from "./pages/RegisterPage";
import VerifyOtpPage from "./pages/VerifyOtpPage";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

/* ================= STATIC PAGES ================= */

import ContactPage from "./pages/ContactPage";
import FAQsPage from "./components/FAQsPage";
import ShippingPage from "./pages/ShippingPage";
import ReturnsPage from "./pages/ReturnsPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsPage from "./pages/TermsPage";

/* ================= CONTEXT ================= */

import { CartProvider, useCart } from "./components/CartContext";
import { AuthProvider, useAuth } from "./components/AuthContext";

/* =========================================================
   PROTECTED ROUTE
========================================================= */

function ProtectedRoute({ children }) {
  const { user, token } = useAuth();
  const location = useLocation();

  if (!token) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (!user) {
    return (
      <div className="loader-container">
        <div className="loader"></div>
        <p>Loading session...</p>
      </div>
    );
  }

  return children;
}

/* =========================================================
   AUTH REDIRECT
========================================================= */

function AuthRedirect({ children }) {
  const { token } = useAuth();
  const { cart } = useCart();

  if (token) {
    if (cart && cart.length > 0) {
      return <Navigate to="/cart" replace />;
    }

    return <Navigate to="/" replace />;
  }

  return children;
}

/* =========================================================
   APP LAYOUT
========================================================= */

function AppLayout() {
  const location = useLocation();

  /* ================= GAME SCREEN DETECTION ================= */

  const isGamePage =
  location.pathname === "/play" ||
  location.pathname === "/coming-soon";

  return (
    <>
      <ScrollToTop />

      {/* HIDE NAVBAR IN GAME */}
      {!isGamePage && <Navbar />}

      {/* ================= ROUTES ================= */}

      <Routes>

        {/* ================= PUBLIC ROUTES ================= */}

        <Route path="/" element={<HomePage />} />

        <Route path="/about" element={<AboutPage />} />

        <Route path="/game" element={<SacMtbGame />} />

        <Route path="/products" element={<ProductsPage />} />

        <Route
          path="/products/:type"
          element={<ProductsPage />}
        />

        <Route path="/toys" element={<Toys />} />

        <Route
          path="/toys/:type"
          element={<Toys />}
        />

      <Route
  path="/coming-soon"
  element={<ComingSoon />}
/>

        {/* ================= GAME PAGE ================= */}

        <Route
          path="/play"
          element={<PlayGame />}
        />

        {/* ================= CONTACT & STATIC ================= */}

        <Route
          path="/contactpage"
          element={<ContactPage />}
        />

        <Route
          path="/faqs"
          element={<FAQsPage />}
        />

        <Route
          path="/shipping"
          element={<ShippingPage />}
        />

        <Route
          path="/returns"
          element={<ReturnsPage />}
        />

        <Route
          path="/privacy-policy"
          element={<PrivacyPolicyPage />}
        />

        <Route
          path="/terms"
          element={<TermsPage />}
        />

        {/* ================= PRODUCT DETAILS ================= */}

        <Route
          path="/productdetailpage/:id"
          element={<ProductDetailPage />}
        />

        {/* ================= AUTH ROUTES ================= */}

        <Route
          path="/register"
          element={
            <AuthRedirect>
              <RegisterPage />
            </AuthRedirect>
          }
        />

        <Route
  path="/verify-otp"
  element={<VerifyOtpPage />}
/>

        <Route
          path="/login"
          element={
            <AuthRedirect>
              <LoginPage />
            </AuthRedirect>
          }
        />

        <Route
          path="/forgot-password"
          element={
            <AuthRedirect>
              <ForgotPasswordPage />
            </AuthRedirect>
          }
        />

        <Route
          path="/reset-password"
          element={
            <AuthRedirect>
              <ResetPasswordPage />
            </AuthRedirect>
          }
        />

        {/* ================= PROTECTED ROUTES ================= */}

        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/my-orders"
          element={
            <ProtectedRoute>
              <UserOrders />
            </ProtectedRoute>
          }
        />

        <Route
          path="/cart"
          element={
            <ProtectedRoute>
              <CartPage />
            </ProtectedRoute>
          }
        />

        <Route
          path="/checkout"
          element={
            <ProtectedRoute>
              <CheckoutPage />
            </ProtectedRoute>
          }
        />

        {/* ================= 404 ================= */}

        <Route
          path="*"
          element={<Navigate to="/" replace />}
        />

      </Routes>

      {/* HIDE FOOTER IN GAME */}
      {!isGamePage && <Footer />}

      {/* HIDE WHATSAPP FLOAT IN GAME */}
      {!isGamePage && <WhatsAppFloat />}
    </>
  );
}

/* =========================================================
   MAIN APP
========================================================= */

function App() {
  return (
    <AuthProvider>

      <CartProvider>

        <Router>

          <AppLayout />

        </Router>

      </CartProvider>

    </AuthProvider>
  );
}

export default App;